import { supabase } from '../lib/supabase'
import { handleQuery, accessDeniedResponse, resolveRoleContext } from './utils'
import { getPresentationById } from './presentationsService'

const TABLE_NAME = 'presentation_views'

const ALLOWED_ROLES = ['agent', 'admin']

/**
 * List view progress for the current user for given presentation ids (for list page progress bars).
 */
export const listMyViewsForPresentations = async (presentationIds, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  if (!roleContext.userId || !ALLOWED_ROLES.includes(roleContext.role)) {
    return { data: [], error: null }
  }

  if (!Array.isArray(presentationIds) || presentationIds.length === 0) {
    return { data: [], error: null }
  }

  const query = supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('user_id', roleContext.userId)
    .in('presentation_id', presentationIds)

  const result = await handleQuery(query, { fallbackMessage: 'Unable to load progress.', allowEmpty: true })
  if (result.error) return result
  return { data: result.data || [], error: null }
}

/**
 * Get view progress for the current user for a presentation.
 */
export const getViewProgress = async (presentationId, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  if (!roleContext.userId || !ALLOWED_ROLES.includes(roleContext.role)) {
    return accessDeniedResponse('Only agents and admins can access presentation views.')
  }

  const { error: presError } = await getPresentationById(presentationId, options)
  if (presError) {
    return { data: null, error: presError }
  }

  const query = supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('presentation_id', presentationId)
    .eq('user_id', roleContext.userId)
    .maybeSingle()

  return handleQuery(query, {
    fallbackMessage: 'Unable to load view progress.',
    allowEmpty: true
  })
}

/**
 * Upsert progress for the current user: last_slide_viewed, completed. RLS ensures user_id = auth.uid().
 */
export const upsertProgress = async (presentationId, { last_slide_viewed, completed }, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  if (!roleContext.userId || !ALLOWED_ROLES.includes(roleContext.role)) {
    return accessDeniedResponse('Only agents and admins can record presentation progress.')
  }

  const { error: presError } = await getPresentationById(presentationId, options)
  if (presError) {
    return { data: null, error: presError }
  }

  const payload = {
    presentation_id: presentationId,
    user_id: roleContext.userId,
    last_slide_viewed: last_slide_viewed ?? 0,
    completed: completed ?? false,
    viewed_at: new Date().toISOString()
  }

  const query = supabase
    .from(TABLE_NAME)
    .upsert(payload, {
      onConflict: 'presentation_id,user_id',
      ignoreDuplicates: false
    })
    .select('*')
    .single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to save progress.',
    allowEmpty: false
  })
}
